import React from "react";
import { Box, Tab, Tabs } from "@mui/material";

interface StyledTabProps {
  label: string;
  value: string | number;
}

interface CustomTabProps {
  tabs: StyledTabProps[];
  value: string | number;
  onChange: (event: React.SyntheticEvent, newValue: string | number) => void;
  centerAligned?: boolean;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const CustomTab: React.FC<CustomTabProps> = ({
  tabs,
  value,
  onChange,
  centerAligned,
}) => {
  // Netflix-esque tokens (visual only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.72)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redHover: "#b20710",
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "calc(100vw - 3rem)",
        overflowX: "auto",
        display: "flex",
        justifyContent: centerAligned ? "center" : "flex-start",
        // dark scrollbars (fallbacks included)
        "&::-webkit-scrollbar": { height: 6 },
        "&::-webkit-scrollbar-track": { backgroundColor: COLORS.bg },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(255,255,255,0.24)",
          borderRadius: 8,
        },
        "-ms-overflow-style": "none",
        "scrollbar-width": "thin",
        "scrollbar-color": "rgba(255,255,255,0.24) transparent",
        // subtle vignette feel behind the tabs
        background: "transparent",
        position: "relative",
      }}
    >
      <Tabs
        value={value}
        onChange={onChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="custom tabs"
        sx={{
          borderRadius: 999,
          border: `1px solid ${COLORS.border}`,
          background: COLORS.panel,
          width: "fit-content",
          px: { xs: 0.5, sm: 0.75 },
          py: { xs: 0.5, sm: 0.75 },
          minHeight: 44,
          boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
          "& .MuiTabs-indicator": { display: "none" },
          "& .MuiTabs-scrollButtons": {
            color: COLORS.muted,
            "&.Mui-disabled": { opacity: 0.28 },
          },
          // space between pills
          "& .MuiTab-root:not(:last-of-type)": {
            mr: { xs: 0.5, sm: 0.75 },
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            disableRipple
            {...a11yProps(index)}
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              fontWeight: 700,
              letterSpacing: "-0.01em",
              fontSize: { xs: 12, sm: 13.5, md: 14 },
              minHeight: 36,
              px: { xs: 1.25, sm: 1.75, md: 2 },
              py: { xs: 0.75, sm: 0.9 },
              borderRadius: 24,
              color: COLORS.muted,
              whiteSpace: "nowrap",
              backgroundColor: "transparent",
              transition: "all .2s ease",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
              },
              "&.Mui-selected": {
                color: "#fff",
                backgroundColor: COLORS.red,
                boxShadow: "0 10px 24px rgba(229,9,20,0.35)",
                "&:hover": { backgroundColor: COLORS.redHover },
              },
              "&.Mui-focusVisible": {
                outline: `2px solid ${COLORS.red}`,
                outlineOffset: 2,
              },
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default CustomTab;
